"use client";

import { Info, Upload, X } from "lucide-react";
import Image from "next/image";
import { useCallback, useState } from "react";
import type { FileRejection } from "react-dropzone";
import { useDropzone } from "react-dropzone";

import { useUser } from "@/hooks/useUser";
import { uploadFile } from "@/lib/storage";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const ACCEPTED_FILE_TYPES = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
} as const;

interface PropertyImagesUploadProps {
  onChange: (value: { url: string; path: string }[]) => void;
  label?: string;
  description?: string;
  tooltip?: {
    trigger: string;
    content: React.ReactNode;
  };
  accept?:
    | keyof typeof ACCEPTED_FILE_TYPES
    | (keyof typeof ACCEPTED_FILE_TYPES)[];
  value?: { url: string; path: string }[];
}

interface FileUploadStatus {
  id: string;
  name: string;
  progress: number;
}

export function ManualPropertyImagesUpload({
  onChange,
  label = "Upload Document",
  description,
  tooltip,
  value = [],
}: PropertyImagesUploadProps) {
  const { user } = useUser();
  const [uploadStatuses, setUploadStatuses] = useState<FileUploadStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [previews, setPreviews] =
    useState<{ url: string; path: string }[]>(value);

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        const errors = rejectedFiles[0].errors;
        if (errors.some((e) => e.code === "file-too-large")) {
          console.error(
            `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`,
          );
        } else if (errors.some((e) => e.code === "file-invalid-type")) {
          console.error("Please upload a PDF, DOC, DOCX, JPG, or PNG file");
        }
        return;
      }

      if (acceptedFiles.length === 0 || isUploading) return;

      setIsUploading(true);

      // Initialize upload statuses for all files
      const newUploadStatuses = acceptedFiles.map((file) => ({
        id: crypto.randomUUID(),
        name: file.name,
        progress: 0,
      }));
      setUploadStatuses(newUploadStatuses);

      try {
        // Create upload promises for each file
        const uploadPromises = acceptedFiles.map(async (file, index) => {
          const fileId = newUploadStatuses[index].id;

          // Simulate upload progress for each file
          const progressInterval = setInterval(() => {
            setUploadStatuses((prev) =>
              prev.map((status) =>
                status.id === fileId
                  ? { ...status, progress: Math.min(status.progress + 10, 90) }
                  : status,
              ),
            );
          }, 100);

          const fileName = `${user?.id}/${Date.now()}_${file.name}`;
          const { url, path } = await uploadFile(
            "properties_media",
            fileName,
            file,
          );
          clearInterval(progressInterval);

          setUploadStatuses((prev) =>
            prev.map((status) =>
              status.id === fileId ? { ...status, progress: 100 } : status,
            ),
          );

          return { url, path };
        });

        const fileResponses = await Promise.all(uploadPromises);
        const updatedFiles = [...previews, ...fileResponses];
        onChange(updatedFiles);
        setPreviews(updatedFiles);
      } catch (error) {
        console.error("Upload failed:", error);
        setUploadStatuses([]);
      } finally {
        setTimeout(() => {
          setIsUploading(false);
          setUploadStatuses([]);
        }, 500);
      }
    },
    [onChange, isUploading],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
    disabled: isUploading,
  });

  return (
    <div className="space-y-2">
      <div className="flex flex-col space-y-1.5">
        {label && (
          <div className="flex items-center gap-1.5">
            <h3 className="font-semibold text-sm">{label}</h3>
            {tooltip && (
              <TooltipProvider>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4 p-0 hover:bg-transparent"
                    >
                      <Info className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="sr-only">More information</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    {tooltip.content}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>

      {previews.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-4">
          {previews.map((file, index) => (
            <div
              key={file.path}
              className="relative aspect-square rounded-lg overflow-hidden border"
            >
              <Image
                src={file.url}
                alt={`Upload preview ${index + 1}`}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 50vw, 33vw"
              />
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  const newPreviews = previews.filter((_, i) => i !== index);
                  setPreviews(newPreviews);
                  onChange(newPreviews);
                }}
              >
                <span className="sr-only">Remove image</span>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div
        {...getRootProps()}
        className={cn(
          "relative border-2 border-dashed rounded-lg transition-colors",
          isDragActive
            ? "border-primary bg-primary/5"
            : "border-muted hover:border-muted-foreground/50 hover:bg-muted/50",
          isUploading && "pointer-events-none",
        )}
      >
        <input {...getInputProps()} />

        <div className="p-4">
          {isUploading ? (
            <div className="space-y-2">
              {uploadStatuses.map((status) => (
                <div key={status.id} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground truncate">
                      {status.name}
                    </span>
                    <span className="text-muted-foreground">
                      {status.progress}%
                    </span>
                  </div>
                  <Progress value={status.progress} className="w-full" />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center space-y-2 text-center py-4">
              <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                <Upload className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-foreground">
                  {isDragActive ? (
                    "Drop your file here"
                  ) : (
                    <>
                      <span className="text-primary">Choose a file</span> or
                      drag and drop
                    </>
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  JPG, or PNG up to 20MB
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
